<template>
  <div class="video-slider" ref="sliderElement">
    <div class="video-slider__progress" :style="style">
      <div class="video-slider__time">{{ currentTime }}</div>
    </div>
  </div>
</template>

<script lang="ts">
import {
  computed, defineComponent, onBeforeUnmount, onMounted, ref,
} from 'vue'
import getOffset from '@/scripts/getOffset'

export default defineComponent({
  name: 'VideoSlider',
  props: {
    duration: {
      type: Number,
      required: true,
    },
    timing: {
      type: Number,
      required: true,
    },
  },
  emits: ['change'],
  setup(props, { emit }) {
    const currentProgress = computed(() => {
      if (!props.duration) {
        return 0
      }
      let r = props.timing / props.duration
      if (r < 0) {
        r = 0
      }
      if (r > 1) {
        r = 1
      }
      return r
    })

    const sliderElement = ref<HTMLDivElement>()

    const currentDrag = ref(0)
    const setCurrentDrag = (x: number) => {
      if (sliderElement.value) {
        const offset = getOffset(sliderElement.value)
        let left = x - offset.left
        if (left < 0) {
          left = 0
        }
        let r = left / sliderElement.value.offsetWidth
        if (r > 1) {
          r = 1
        }
        currentDrag.value = r
      }
    }

    const getX = (e: MouseEvent | TouchEvent) => {
      const te = e as TouchEvent
      if (te.touches && te.touches.length) {
        return te.touches[0].clientX
      }
      return (e as MouseEvent).clientX
    }

    const isDragging = ref(false)
    const dragStart = (e: MouseEvent | TouchEvent) => {
      isDragging.value = true
      setCurrentDrag(getX(e))
    }
    const dragMove = (e: MouseEvent | TouchEvent) => {
      if (isDragging.value) {
        setCurrentDrag(getX(e))
      }
    }
    const dragEnd = () => {
      if (isDragging.value) {
        isDragging.value = false
        emit('change', currentDrag.value * props.duration)
      }
    }
    onMounted(() => {
      sliderElement.value?.addEventListener('mousedown', dragStart)
      sliderElement.value?.addEventListener('touchstart', dragStart)
      document.addEventListener('mousemove', dragMove)
      document.addEventListener('touchmove', dragMove)
      document.addEventListener('mouseup', dragEnd)
      document.addEventListener('touchend', dragEnd)
    })
    onBeforeUnmount(() => {
      sliderElement.value?.removeEventListener('mousedown', dragStart)
      sliderElement.value?.removeEventListener('touchstart', dragStart)
      document.removeEventListener('mousemove', dragMove)
      document.removeEventListener('touchmove', dragMove)
      document.removeEventListener('mouseup', dragEnd)
      document.removeEventListener('touchend', dragEnd)
    })

    const style = computed(() => ({
      width: `${(isDragging.value ? currentDrag.value : currentProgress.value) * 100}%`,
    }))

    const currentTime = computed(() => {
      if (!props.duration) {
        return ''
      }
      const r = isDragging.value ? currentDrag.value : currentProgress.value
      const t = Math.floor(props.duration * r)
      const hours = Math.floor(t / 3600)
      let minutes = Math.floor(t / 60)
      const seconds = t - (minutes * 60)
      minutes -= hours * 60
      let time = ''
      if (hours) {
        time += `${hours}:`
      }
      time += `${minutes < 10 ? 0 : ''}${minutes}:${seconds < 10 ? 0 : ''}${seconds}`
      return time
    })

    return {
      currentTime,
      sliderElement,
      style,
    }
  },
})
</script>

<style lang="stylus">
.video-slider
  size 100% 30px
  absolute left bottom
  z-index 20
  background var(--dark-bg-color)
  user-select none

  &__progress
    absolute left top
    size 100%
    background var(--button-color)
    display flex
    justify-content flex-end
    align-items center
    user-select none

  &__time
    color var(--button-text-color)
    text-align left
    max-width 100%

@media screen and (min-width: 940px)
  .video-slider
    cursor pointer
</style>
